import {
	evaluateValueMapping,
	formatDuration,
	getMappedDisplayValue,
} from '@supply-mover-2-0/core';
import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import type { EnhancedFieldConfiguration } from '../../types';
import { evaluateColorRule } from '../../utils/colorRuleEngine';

/**
 * Gets the display value, icon name, and color for a field, applying value mapping if configured
 * @param field The field configuration
 * @param value The raw value from data
 * @param data The full row data object (optional, needed for dynamic/conditional color rules)
 * @returns Object containing displayValue, iconName, and colorValue
 */
export function getFieldDisplayProperties(
	field: EnhancedFieldConfiguration,
	value: any,
	data?: Record<string, any>,
) {
	const { styleConfig, dataConfig } = field;

	// Apply custom formatters first, before value mapping
	let formattedValue = value;
	if (styleConfig?.formatOptions?.customFormat) {
		switch (styleConfig.formatOptions.customFormat) {
			case 'duration-seconds':
				formattedValue = formatDuration(value);
				break;
			// Add more custom formats as needed
		}
	}

	// Then apply value mapping to get display text, icon, and potential color
	const mappingResult = evaluateValueMapping(
		styleConfig?.valueMapping,
		formattedValue,
	);

	// Get the display value - prioritize mapped text, then formatted value, then default
	// If there's a mapped icon, we might want to show that instead of text
	const displayValue =
		mappingResult.displayText !== undefined
			? mappingResult.displayText
			: formattedValue != null &&
				  (typeof formattedValue !== 'string' || formattedValue.trim() !== '')
				? formattedValue
				: (dataConfig?.defaultValue ?? '');

	// Get mapped icon name
	const iconName = mappingResult.iconName;

	// For range-mapping color rules, pass the resolved numeric value
	// so the engine can match it against configured ranges
	const colorData = data != null ? { ...data, __resolvedValue: value } : data;

	return {
		displayValue,
		iconName,
		colorValue:
			mappingResult.color ??
			evaluateColorRule(styleConfig?.colorRule, colorData),
	};
}

/**
 * Gets just the mapped display value for a field
 * @param field The field configuration
 * @param value The raw value
 * @returns The display value after applying mapping
 */
export function getDisplayValue(
	field: EnhancedFieldConfiguration,
	value: any,
): any {
	const { dataConfig, styleConfig } = field;
	return (
		getMappedDisplayValue(value, styleConfig?.valueMapping) ??
		dataConfig?.defaultValue ??
		''
	);
}

/**
 * Resolves the text color for a TextDisplay field.
 * When a field has linking enabled but the link cannot be resolved for a given row
 * (e.g. "Not Linked"), the default text color is used instead of the colorRule color.
 * This prevents non-clickable text from appearing styled as a link.
 */
export function resolveTextDisplayColor(params: {
	isLinkEnabled: boolean;
	linkConfigEnabled: boolean;
	colorText: string | null | undefined;
	isDark: boolean;
	primaryColor: string;
	defaultTextColor: string;
}): string {
	const {
		isLinkEnabled,
		linkConfigEnabled,
		colorText,
		isDark,
		primaryColor,
		defaultTextColor,
	} = params;

	if (isLinkEnabled) {
		return primaryColor;
	}

	if (linkConfigEnabled && !isLinkEnabled) {
		return defaultTextColor;
	}

	return resolveThemeColor(colorText, isDark) ?? defaultTextColor;
}
